import { memo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { MAX_DAILY_WINS, MAX_PARTICIPANTS_TO_START, MAX_REPEAT_TIMES, MAX_VOUCHER_COUNT_PER_WINNER, MAX_WINNERS_PER_GIVEAWAY, MIN_DAILY_WINS, MIN_PARTICIPANTS_TO_START, MIN_REPEAT_TIMES, MIN_VOUCHER_COUNT_PER_WINNER, MIN_WINNERS_PER_GIVEAWAY, DEFAULT_WAIT_TIME, WAIT_TIME_PRESETS, } from 'root/src/shared/giveaways/giveaways.constants';
import { useCreatorData } from 'root/src/client/logic/api/hooks/useCreatorData';
import { Switch } from 'root/src/client/v2/common/components/ui/switch';
import { RedesignStepper } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/RedesignStepper';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const collapse = (reduceMotion) => ({
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' },
});
const SettingRow = ({ htmlFor, label, helper, sub, children }) => (<div className={cn('flex items-center justify-between gap-3 py-2', sub && 'pl-3')}>
		<label htmlFor={htmlFor} className="flex cursor-pointer flex-col gap-0.5">
			<span className="text-[13px] font-medium leading-tight text-foreground">{label}</span>
			<span className="text-[11px] leading-tight text-muted-foreground">{helper}</span>
		</label>
		<div className="shrink-0">{children}</div>
	</div>);
const SwitchRow = ({ name, label, helper, sub }) => {
    const form = useFormContext();
    return (<Controller name={name} control={form.control} render={({ field }) => (<SettingRow htmlFor={name} label={label} helper={helper} sub={sub}>
					<Switch id={name} checked={Boolean(field.value)} onCheckedChange={field.onChange}/>
				</SettingRow>)}/>);
};
const StepperRow = ({ name, label, helper, min, max, fallback, sub }) => {
    const form = useFormContext();
    return (<Controller name={name} control={form.control} render={({ field }) => (<SettingRow htmlFor={name} label={label} helper={helper} sub={sub}>
					<RedesignStepper min={min} max={max} value={typeof field.value === 'number' ? field.value : (fallback !== null && fallback !== void 0 ? fallback : min)} onChange={field.onChange}/>
				</SettingRow>)}/>);
};
const WaitTimeRow = ({ sub }) => {
    const form = useFormContext();
    return (<Controller name="maxWaitTime" control={form.control} render={({ field }) => (<SettingRow htmlFor="maxWaitTime" label="Max wait time" helper="How long to wait for participants" sub={sub}>
					<select id="maxWaitTime" value={typeof field.value === 'number' ? field.value : DEFAULT_WAIT_TIME} onChange={event => field.onChange(Number(event.target.value))} className="rounded-lg border border-solid border-white/[0.08] bg-secondary px-3 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none">
						{WAIT_TIME_PRESETS.map(preset => (<option key={preset.value} value={preset.value}>{preset.label}</option>))}
					</select>
				</SettingRow>)}/>);
};
const AnimatedSub = ({ show, reduceMotion, children }) => (<AnimatePresence initial={false}>
		{show && (<motion.div {...collapse(reduceMotion)} className="overflow-hidden">
				{children}
			</motion.div>)}
	</AnimatePresence>);
// Fresh redesign advanced settings: animated collapsible with redesign switches + steppers
// (replaces the legacy AdvancedSettingsSection). Reuses the same form fields + constants.
export const RedesignGiveawayAdvancedSettings = memo(() => {
    const [isOpen, setIsOpen] = useState(false);
    const reduceMotion = useReducedMotion();
    const form = useFormContext();
    const creatorData = useCreatorData();
    const isPartnerOrAffiliate = (creatorData === null || creatorData === void 0 ? void 0 : creatorData.broadcasterType) === 'partner' || (creatorData === null || creatorData === void 0 ? void 0 : creatorData.broadcasterType) === 'affiliate';
    const isMaxDailyWinsEnabled = form.watch('isMaxDailyWinsEnabled');
    const isMinParticipantsEnabled = form.watch('isMinParticipantsEnabled');
    const isRepeatingEnabled = form.watch('isRepeatingEnabled');
    return (<div className="w-full">
			<button type="button" onClick={() => setIsOpen(open => !open)} className="flex w-full items-center justify-between rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] px-3.5 py-2.5 text-[13px] font-medium text-foreground transition-colors hover:bg-white/[0.04]">
				<span className="flex items-center gap-2">
					<TuneRoundedIcon sx={{ fontSize: 18 }} className="text-muted-foreground"/>
					Advanced settings
				</span>
				<motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: reduceMotion ? 0 : 0.2 }} className="flex">
					<ExpandMoreRoundedIcon sx={{ fontSize: 20 }} className="text-muted-foreground"/>
				</motion.span>
			</button>

			<AnimatePresence initial={false}>
				{isOpen && (<motion.div {...collapse(reduceMotion)} className="overflow-hidden">
						<div className="mt-2 flex flex-col divide-y divide-white/[0.06] rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] px-3.5">
							{isPartnerOrAffiliate && (<SwitchRow name="isTwitchSubscribersOnly" label="Subscriber preference" helper="Reward your Twitch subscribers with bonus entries"/>)}
							<SwitchRow name="blockBannedUsers" label="Block banned users" helper="Prevent banned users from entering"/>
							<StepperRow name="maxWinners" label="Number of winners" helper="How many winners to select" min={MIN_WINNERS_PER_GIVEAWAY} max={MAX_WINNERS_PER_GIVEAWAY} fallback={1}/>
							<StepperRow name="voucherCountPerWinner" label="Rewards per winner" helper="How many rewards each winner receives" min={MIN_VOUCHER_COUNT_PER_WINNER} max={MAX_VOUCHER_COUNT_PER_WINNER} fallback={1}/>

							<SwitchRow name="isMaxDailyWinsEnabled" label="Daily wins limit" helper="Cap how many times a user can win per day"/>
							<AnimatedSub show={Boolean(isMaxDailyWinsEnabled)} reduceMotion={reduceMotion}>
								<StepperRow name="maxDailyUserWins" label="Max wins per day" helper="Per user, each day" min={MIN_DAILY_WINS} max={MAX_DAILY_WINS} fallback={1} sub/>
							</AnimatedSub>

							<SwitchRow name="isMinParticipantsEnabled" label="Minimum participants" helper="Cancel the giveaway if too few enter"/>
							<AnimatedSub show={Boolean(isMinParticipantsEnabled)} reduceMotion={reduceMotion}>
								<StepperRow name="minParticipantsToStart" label="Min participants" helper="Required to resolve" min={MIN_PARTICIPANTS_TO_START} max={MAX_PARTICIPANTS_TO_START} fallback={1} sub/>
								<WaitTimeRow sub/>
							</AnimatedSub>

							<SwitchRow name="isRepeatingEnabled" label="Repeat automatically" helper="Run this giveaway on a schedule"/>
							<AnimatedSub show={Boolean(isRepeatingEnabled)} reduceMotion={reduceMotion}>
								<StepperRow name="repeatTimes" label="Repeat times" helper="How many times to repeat" min={MIN_REPEAT_TIMES} max={MAX_REPEAT_TIMES} fallback={1} sub/>
							</AnimatedSub>
						</div>
					</motion.div>)}
			</AnimatePresence>
		</div>);
});
